/**
 * @fileoverview Individual conversation list item component.
 * Displays phone number, last message preview, timestamp, and status indicators.
 */

import { formatDistanceToNow } from 'date-fns';
import { MessageSquare, Phone, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatPhoneNumber } from '@/hooks/useMessages';
import type { ConversationThread } from '@/types/message';

/**
 * Props for ConversationListItem component.
 */
interface ConversationListItemProps {
  /** The conversation thread data to display */
  thread: ConversationThread;
  /** Whether this item is currently selected */
  isSelected: boolean;
  /** Click handler for selection */
  onClick: () => void;
}

/**
 * Renders a single conversation thread item with message preview and metadata.
 */
export function ConversationListItem({ thread, isSelected, onClick }: ConversationListItemProps) {
  const lastMessage = thread.messages[0];
  const isInbound = lastMessage?.message_direction === 'inbound';

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left p-4 border-b border-border transition-colors',
        'hover:bg-accent/50',
        isSelected && 'bg-accent'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-foreground truncate">
                {formatPhoneNumber(thread.phone_number)}
              </span>
              {thread.linked_call_id && (
                <Phone className="w-3 h-3 text-muted-foreground flex-shrink-0" />
              )}
            </div>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              {isInbound ? (
                <ArrowDownLeft className="w-3 h-3 text-green-500" />
              ) : (
                <ArrowUpRight className="w-3 h-3 text-blue-500" />
              )}
              <span className="truncate">{thread.last_message_preview}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(thread.last_message_at), { addSuffix: true })}
          </span>
          {thread.unread_count > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded-full">
              {thread.unread_count}
            </span>
          )}
          {thread.has_inbound && (
            <span className="text-xs text-green-600 dark:text-green-400">Has replies</span>
          )}
        </div>
      </div>
    </button>
  );
}
